import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import { alpha } from '@mui/material/styles'
import type { Components, Theme } from '@mui/material/styles'
import type { OverridesStyleRules } from '@mui/material/styles/overrides'
import { TabPanelProps, TabPanelClassKey } from '@mui/lab/TabPanel'
import { PILL_RADIUS } from './shape'

declare module '@mui/material/styles' {
  interface Components<Theme = unknown> {
    MuiTabPanel: {
      defaultProps?: TabPanelProps
      styleOverrides?: Partial<OverridesStyleRules<TabPanelClassKey, 'MuiTabPanel', Theme>>
    }
  }
}

export const components: Components<Theme> = {
  MuiAccordion: {
    defaultProps: {
      variant: 'outlined',
    },
  },
  MuiAccordionDetails: {
    styleOverrides: {
      root: ({ theme }) => ({
        padding: theme.spacing(2, 3),
        [theme.breakpoints.down('sm')]: {
          padding: theme.spacing(1, 2),
        },

        borderTop: `1px solid ${theme.palette.divider}`,
      }),
    },
  },
  MuiAccordionSummary: {
    defaultProps: {
      expandIcon: <ExpandMoreIcon />,
    },
    styleOverrides: {
      root: ({ theme }) => ({
        padding: theme.spacing(1, 3),
        [theme.breakpoints.down('sm')]: {
          padding: theme.spacing(1, 2),
        },
      }),
    },
  },
  MuiAccordionActions: {
    styleOverrides: {
      root: ({ theme }) => ({
        ['> :not(:first-child)']: {
          marginLeft: theme.spacing(1),
        },
        padding: theme.spacing(1, 3),
        [theme.breakpoints.down('sm')]: {
          padding: theme.spacing(1, 2),
        },
      }),
    },
  },
  MuiAppBar: {
    defaultProps: {
      elevation: 0,
      color: 'default',
    },
    styleOverrides: {
      root: ({ theme }) => ({
        // The app bar used to be a full-width slab of brand orange. It carried no
        // information, dominated every screen, and left the wordmark sitting on a
        // 2.8:1 ground. The chrome is now a surface and the orange is spent on
        // actions instead.
        //
        // Fully transparent, so the ambient field on the body runs through the
        // toolbar unbroken. A translucent fill here — even at 82% — repainted
        // that strip toward paper and made the gradient appear to restart below
        // the bar.
        //
        // There is nothing to blur: `Content` starts at the toolbar's bottom edge
        // and scrolls inside its own box, so content never passes underneath.
        color: theme.palette.text.primary,
        backgroundImage: 'none',
        backgroundColor: theme.palette.isHighContrast ? theme.palette.background.default : 'transparent',
      }),
    },
  },
  MuiAvatar: {
    styleOverrides: {
      root: ({ theme }) => ({
        backgroundColor: theme.palette.neutral[500],
        color: theme.palette.getContrastText(theme.palette.neutral[500]),
        fontSize: 14,
        fontWeight: 600,
        letterSpacing: 0,
      }),
    },
  },
  MuiButton: {
    defaultProps: {
      disableElevation: true,
    },
    styleOverrides: {
      root: {
        textTransform: 'none',
      },
      // The primary action carries the brand orange flat. Its finish is
      // elevation and a crisp edge, not a highlight painted across the face.
      containedPrimary: ({ theme }) =>
        theme.palette.isHighContrast
          ? {}
          : {
              boxShadow: `inset 0 0 0 1px ${alpha(theme.palette.common.black, 0.1)}, ${theme.shadows[2]}`,
              '&:hover': {
                boxShadow: `inset 0 0 0 1px ${alpha(theme.palette.common.black, 0.1)}, ${theme.shadows[5]}`,
              },
              '&:active': {
                boxShadow: `inset 0 0 0 1px ${alpha(theme.palette.common.black, 0.14)}`,
              },
            },
      sizeLarge: ({ theme }) => ({
        padding: theme.spacing(1.5, 3),
        [theme.breakpoints.down('sm')]: {
          padding: theme.spacing(1, 2.5),
        },
      }),
      sizeMedium: ({ theme }) => ({
        padding: theme.spacing(1, 2.5),
        [theme.breakpoints.down('sm')]: {
          padding: theme.spacing(0.75, 2),
        },
      }),
      sizeSmall: ({ theme }) => ({
        padding: theme.spacing(0.75, 2),
        [theme.breakpoints.down('sm')]: {
          padding: theme.spacing(0.75, 1),
        },
      }),
      textSizeLarge: ({ theme }) => ({
        padding: theme.spacing(1.5, 2),
      }),
      textSizeMedium: ({ theme }) => ({
        padding: theme.spacing(1, 2),
      }),
      textSizeSmall: ({ theme }) => ({
        padding: theme.spacing(0.75, 1.5),
      }),
    },
  },
  MuiButtonBase: {
    defaultProps: {
      disableRipple: true,
    },
    styleOverrides: {
      root: ({ theme }) => ({
        // MUI removes the native focus ring on every ButtonBase, and normally
        // gives it back through the ripple. The ripple is disabled here and
        // `MuiButton.disableElevation` cancels the contained-button fallback, so
        // without this rule keyboard users get no focus affordance at all.
        '&.Mui-focusVisible': {
          // Brand orange on the dark grounds, its darkened partner on light —
          // both clear 3:1 against the surfaces they land on. The white/black
          // halo underneath keeps the ring visible where it crosses a filled
          // control of the same hue.
          outline: `2px solid ${
            theme.palette.mode === 'light' ? theme.palette.primary.dark : theme.palette.primary.main
          }`,
          outlineOffset: 2,
          boxShadow: `0 0 0 4px ${alpha(theme.palette.background.default, 0.9)}`,
        },
      }),
    },
  },
  MuiCard: {
    defaultProps: {
      variant: 'outlined',
    },
    styleOverrides: {
      root: ({ theme }) => ({
        // Cards sit a step above the control radius so a container never shares a
        // corner with the buttons inside it.
        borderRadius: theme.shape.borderRadius * 1.6,
        // No surface gradient. A highlight fading down from the top edge is the
        // old glossy-desktop treatment; separation here comes from the card's
        // value against the page ground, a hairline, and elevation.
        backgroundImage: 'none',
        boxShadow: theme.palette.isHighContrast ? 'none' : theme.shadows[1],
        transition: theme.transitions.create(['box-shadow', 'border-color'], {
          duration: theme.transitions.duration.shorter,
        }),
      }),
    },
  },
  MuiCardActions: {
    styleOverrides: {
      root: ({ theme }) => ({
        justifyContent: 'flex-end',
        padding: theme.spacing(2, 3),

        [theme.breakpoints.down('sm')]: {
          padding: theme.spacing(1, 2),
        },
      }),
    },
  },
  MuiCardContent: {
    styleOverrides: {
      root: ({ theme }) => ({
        '&:first-of-type': {
          paddingTop: theme.spacing(2),
        },
        '&:last-of-type': {
          paddingBottom: theme.spacing(2),
        },
        padding: theme.spacing(1, 3),

        [theme.breakpoints.down('sm')]: {
          padding: theme.spacing(1, 2),
        },
      }),
    },
  },
  MuiCardHeader: {
    defaultProps: {
      subheaderTypographyProps: {
        variant: 'body2',
      },
      titleTypographyProps: {
        variant: 'h6',
      },
    },
    styleOverrides: {
      root: ({ theme }) => ({
        padding: theme.spacing(3),
        [theme.breakpoints.down('sm')]: {
          padding: theme.spacing(2),
          // This looks a bit better on mobile devices but currently breaks any buttons that are absolute positioned within the card header
          // padding: theme.spacing(2, 2, 1, 2),
        },
      }),
    },
  },
  MuiCheckbox: {
    defaultProps: {
      color: 'primary',
    },
  },
  MuiChip: {
    defaultProps: { size: 'small' },
    styleOverrides: {
      root: ({ theme }) => ({
        '&.MuiChip-filledDefault': {
          '& .MuiChip-deleteIcon': {
            color: theme.palette.mode === 'dark' ? theme.palette.neutral[500] : theme.palette.neutral[400],
          },
          backgroundColor: theme.palette.mode === 'dark' ? theme.palette.neutral[800] : theme.palette.neutral[200],
        },
        '&.MuiChip-outlinedDefault': {
          '& .MuiChip-deleteIcon': {
            color: theme.palette.mode === 'dark' ? theme.palette.neutral[700] : theme.palette.neutral[300],
          },
          borderColor: theme.palette.mode === 'dark' ? theme.palette.neutral[700] : undefined,
        },
        fontWeight: 500,
        // Chips are labels, not controls — a pill reads as a tag and stops them
        // being mistaken for the small buttons they used to resemble.
        borderRadius: PILL_RADIUS,
        // The default variant doesn't have an outline so we add one in high contrast mode
        border: theme.palette.isHighContrast ? `1px solid ${theme.palette.divider}` : undefined,
      }),
    },
  },
  MuiCssBaseline: {
    styleOverrides: (theme) => ({
      '*': {
        boxSizing: 'border-box',
      },
      body: {
        display: 'flex',
        flex: '1 1 auto',
        flexDirection: 'column',
        minHeight: '100%',
        width: '100%',
        // One ambient wash owning the whole canvas, rather than accents sprinkled
        // over individual elements. Two very low-chroma pools — brand orange from
        // the top left, the secondary cyan answering from the right — sit behind
        // every screen and never touch a text or control surface, so they add
        // atmosphere without moving any contrast ratio.
        //
        // Fixed attachment: the app scrolls inside its own container, so the wash
        // stays put and reads as the room the interface is in rather than as
        // content moving past.
        ...(theme.palette.isHighContrast
          ? {}
          : {
              backgroundImage: [
                `radial-gradient(1100px 620px at 12% -8%, ${alpha(
                  theme.palette.primary.main,
                  theme.palette.mode === 'dark' ? 0.1 : 0.07,
                )}, transparent 62%)`,
                `radial-gradient(900px 520px at 92% 4%, ${alpha(
                  theme.palette.secondary.main,
                  theme.palette.mode === 'dark' ? 0.07 : 0.05,
                )}, transparent 58%)`,
              ].join(', '),
              backgroundAttachment: 'fixed',
              backgroundRepeat: 'no-repeat',
            }),
      },
      html: {
        display: 'flex',
        flexDirection: 'column',
        minHeight: '100%',
        MozOsxFontSmoothing: 'grayscale',
        WebkitFontSmoothing: 'antialiased',
        width: '100%',
      },
      ':root': {
        colorScheme: theme.palette.mode,
      },
    }),
  },
  MuiDialog: {
    styleOverrides: {
      paper: ({ theme }) => ({
        [theme.breakpoints.down('sm')]: {
          marginLeft: theme.spacing(2),
          marginRight: theme.spacing(2),
        },
      }),
      paperFullScreen: ({ theme }) => ({
        [theme.breakpoints.down('sm')]: {
          marginLeft: 0,
          marginRight: 0,
        },
      }),
    },
  },
  MuiDialogTitle: {
    styleOverrides: {
      root: ({ theme }) => ({
        justifyContent: 'flex-end',
        padding: theme.spacing(2, 3),

        [theme.breakpoints.down('sm')]: {
          padding: theme.spacing(2),
        },
      }),
    },
  },
  MuiDialogContent: {
    styleOverrides: {
      root: ({ theme }) => ({
        '&:first-of-type': {
          paddingTop: theme.spacing(1),
        },
        '&:last-of-type': {
          paddingBottom: theme.spacing(1),
        },
        padding: theme.spacing(1, 3),

        [theme.breakpoints.down('sm')]: {
          padding: theme.spacing(1, 2),
        },
      }),
    },
  },
  MuiDialogActions: {
    styleOverrides: {
      root: ({ theme }) => ({
        justifyContent: 'flex-end',
        padding: theme.spacing(2, 3),

        [theme.breakpoints.down('sm')]: {
          padding: theme.spacing(2),
        },
      }),
    },
  },
  MuiIconButton: {
    styleOverrides: {
      // Attendix is used on tablets in the room, so icon buttons carry a 44px
      // minimum hit area. The visual box stays at its old size — the target is
      // grown, not the ornament.
      root: {
        borderRadius: 8,
        padding: 8,
        minWidth: 44,
        minHeight: 44,
      },
      sizeSmall: {
        padding: 6,
        minWidth: 40,
        minHeight: 40,
      },
    },
  },
  MuiInputBase: {
    styleOverrides: {
      input: ({ theme }) => ({
        '&::placeholder': {
          color: theme.palette.text.secondary,
          opacity: 1,
        },
      }),
    },
  },
  MuiLinearProgress: {
    styleOverrides: {
      root: {
        borderRadius: 3,
        overflow: 'hidden',
      },
    },
  },
  MuiLink: {
    defaultProps: {
      underline: 'hover',
      color: 'primary',
    },
    styleOverrides: {
      root: ({ theme }) => ({
        // Brand orange is only 2.79:1 on a light ground, so links take the
        // darkened partner there. On dark grounds the orange already passes.
        color: theme.palette.mode === 'light' ? theme.palette.primary.dark : theme.palette.primary.main,
      }),
    },
  },
  MuiListItem: {
    styleOverrides: {
      root: ({ theme }) => ({
        paddingLeft: theme.spacing(3),
        paddingRight: theme.spacing(3),
        [theme.breakpoints.down('sm')]: {
          paddingLeft: theme.spacing(2),
          paddingRight: theme.spacing(2),
        },
      }),
    },
  },
  MuiDrawer: {
    styleOverrides: {
      paper: ({ theme }) => ({
        backgroundImage: 'none',
        // The shell reads as one surface behind the content rather than a panel
        // bolted to its left, so it takes the page ground rather than paper.
        backgroundColor: theme.palette.background.default,
        borderRight: `1px solid ${theme.palette.divider}`,
      }),
      // The docked sidebar is part of the page, so the ambient wash on the body
      // has to run behind it. Left opaque, it cut the wash off at the drawer edge
      // and put a seam down the side of every screen. The temporary drawer keeps
      // its own ground — it floats over content and has to stay readable.
      paperAnchorDockedLeft: ({ theme }) => ({
        backgroundColor: theme.palette.isHighContrast ? theme.palette.background.default : 'transparent',
      }),
    },
  },
  MuiListSubheader: {
    styleOverrides: {
      root: {
        // MUI defaults these to `background.paper`, which used to match the drawer
        // and now bands against it.
        backgroundColor: 'transparent',
      },
    },
  },
  MuiListItemButton: {
    styleOverrides: {
      root: ({ theme }) => ({
        transition: theme.transitions.create('background-color', { duration: 150 }),
        paddingLeft: theme.spacing(3),
        paddingRight: theme.spacing(3),
        [theme.breakpoints.down('sm')]: {
          paddingLeft: theme.spacing(2),
          paddingRight: theme.spacing(2),
        },
      }),
    },
  },
  MuiListItemIcon: {
    styleOverrides: {
      root: ({ theme }) => ({
        '&.MuiListItemIcon-root': {
          minWidth: 'unset',
        },
        marginRight: theme.spacing(2),
      }),
    },
  },
  MuiMenu: {
    styleOverrides: {
      paper: ({ theme }) => ({
        borderColor: theme.palette.divider,
        borderStyle: 'solid',
        borderWidth: 1,
      }),
    },
  },
  MuiOutlinedInput: {
    styleOverrides: {
      input: {
        fontWeight: 500,
      },
      notchedOutline: ({ theme }) => ({
        borderColor: theme.palette.divider,
      }),
    },
  },
  MuiPaper: {
    styleOverrides: {
      root: {
        backgroundImage: 'none',
      },
    },
  },
  MuiPopover: {
    defaultProps: {
      elevation: 16,
    },
    styleOverrides: {
      paper: ({ theme }) => ({
        borderColor: theme.palette.divider,
        borderStyle: 'solid',
        borderWidth: 1,
      }),
    },
  },
  MuiRadio: {
    defaultProps: {
      color: 'primary',
    },
  },
  MuiSwitch: {
    defaultProps: {
      color: 'primary',
    },
    styleOverrides: {
      switchBase: ({ theme }) => ({
        color: theme.palette.mode === 'dark' ? theme.palette.neutral[700] : theme.palette.neutral[500],
      }),
      track: ({ theme }) => ({
        backgroundColor: theme.palette.mode === 'dark' ? theme.palette.neutral[500] : theme.palette.neutral[400],
        opacity: 1,
      }),
    },
  },
  MuiTab: {
    styleOverrides: {
      root: ({ theme }) => ({
        '& + &': {
          marginLeft: theme.spacing(3),
        },
        fontSize: 14,
        fontWeight: 500,
        lineHeight: 1.71,
        minWidth: 'auto',
        paddingLeft: 0,
        paddingRight: 0,
        textTransform: 'none',
        overflow: 'visible', // Allow badges to be displayed around the tab labels without clipping
      }),
    },
  },
  MuiTabPanel: {
    styleOverrides: {
      root: {
        padding: 0,
      },
    },
  },
  MuiTableCell: {
    styleOverrides: {
      root: ({ theme }) => ({
        borderBottom: `1px solid ${theme.palette.divider}`,
        ['&:first-of-type']: {
          paddingLeft: theme.spacing(3),

          [theme.breakpoints.down('sm')]: {
            paddingLeft: theme.spacing(2),
          },
        },
        ['&:last-of-type']: {
          paddingRight: theme.spacing(3),

          [theme.breakpoints.down('sm')]: {
            paddingRight: theme.spacing(2),
          },
        },
      }),
    },
  },
  MuiTableHead: {
    styleOverrides: {
      root: ({ theme }) => ({
        '.MuiTableCell-root': {
          color: theme.palette.mode === 'dark' ? theme.palette.neutral[300] : theme.palette.neutral[700],
          padding: theme.spacing(1.5, 2),
        },
        '& .MuiTableCell-paddingCheckbox': {
          paddingBottom: theme.spacing(0.5),
          paddingTop: theme.spacing(0.5),
        },
        '& .MuiTableCell-root': {
          fontSize: '12px',
          fontWeight: 600,
          letterSpacing: 0.5,
          lineHeight: 1,
          textTransform: 'uppercase',
        },
        backgroundColor: theme.palette.mode === 'dark' ? theme.palette.neutral[900] : theme.palette.neutral[100],
      }),
    },
  },
  MuiTextField: {
    defaultProps: {
      fullWidth: true,
      margin: 'normal',
    },
  },
  MuiSlider: {
    styleOverrides: {
      valueLabel: ({ ownerState, theme }) => ({
        backgroundColor: 'transparent',
        color: theme.palette.text.secondary,
        ...(ownerState.orientation === 'vertical' && {
          left: '2rem',
        }),
      }),
    },
  },
}
